/**
 * XLSX Parser for SHOPLINE 1.0 format
 * Handles Excel 2007+ (.xlsx) files exported from SHOPLINE 1.0 (HK/TW platforms)
 */

import * as XLSX from 'xlsx';

// SHOPLINE 1.0 signature column name for detection
const SHOPLINE_V1_SIGNATURE = 'Product ID (DO NOT EDIT)';

/**
 * Parse boolean value from SHOPLINE 1.0 Y/N format
 * @param {string} value - Y or N
 * @returns {boolean|null}
 */
function parseBool(value) {
  if (value === 'Y') return true;
  if (value === 'N') return false;
  return null;
}

/**
 * Parse numeric value safely
 * @param {*} value - Value to parse
 * @param {number} defaultValue - Default value if parsing fails
 * @returns {number}
 */
function parseNumeric(value, defaultValue = 0) {
  if (value === null || value === undefined || value === '') return defaultValue;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Parse integer value safely
 * @param {*} value - Value to parse
 * @param {number} defaultValue - Default value if parsing fails
 * @returns {number}
 */
function parseIntValue(value, defaultValue = 0) {
  if (value === null || value === undefined || value === '') return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Parse tags/collections string into array
 * @param {string} tagsString - Comma-separated tags
 * @returns {string[]} - Array of tags
 */
function parseTags(tagsString) {
  if (!tagsString) return [];
  return tagsString
    .split(',')
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0);
}

/**
 * Generate handle from product title
 * SHOPLINE 1.0 doesn't have handle field, so we generate from title
 * @param {string} title - Product title
 * @param {number} index - Row index for uniqueness
 * @returns {string} - Generated handle
 */
function generateHandle(title, index) {
  if (!title) return `product-${index}`;

  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .substring(0, 50); // Limit length
}

/**
 * Normalize a raw XLSX row to unified product format
 * @param {Object} row - Raw XLSX row
 * @param {number} index - Row index
 * @returns {Object} - Normalized product object
 */
function normalizeRow(row, index) {
  const title = row['Product Name (English)'] || row['Product Name (Traditional Chinese)'] || 'Untitled Product';
  const handle = generateHandle(title, index);

  // Parse images - SHOPLINE 1.0 may have multiple image columns or comma-separated
  const images = [];
  const mainImage = row['Image'] || row['Images'];
  if (mainImage) {
    // Try to split by comma if multiple images
    const imageList = mainImage.split(',').map(img => img.trim()).filter(img => img);
    images.push(...imageList);
  }

  // Build options array for variants (if variant exists)
  const options = [];
  const variantName = row['Variant (English) (DO NOT EDIT)'] || row['Variant (Traditional Chinese) (DO NOT EDIT)'];
  if (variantName) {
    options.push({
      name: 'Variant',
      position: 1,
      value: variantName
    });
  }

  // Parse tags and collections
  const tags = parseTags(row['Product Tag']);
  const collections = parseTags(row['Online Store Categories']);

  return {
    // ========== Core Fields ==========
    id: row['Product ID (DO NOT EDIT)'] || `shopline-v1-product-${index}`,
    semantic_id: handle || `product-${index}`,
    handle: handle,
    title: title,
    subtitle: null, // SHOPLINE 1.0 doesn't have subtitle
    description: row['Product Summary (Traditional Chinese)'] || row['Product Summary (English)'] || null,
    price: parseNumeric(row['Regular Price']),
    compare_at_price: parseNumeric(row['Sale Price'], null),
    stock: parseIntValue(row['Quantity (DO NOT EDIT)']),
    brand: row['Brand'] || null,
    spu: null,

    // ========== Variant Fields ==========
    sku: row['SKU'] || row['Variant SKU'] || null,
    options: options,
    option1_name: variantName ? 'Variant' : null,
    option1_value: variantName || null,
    option2_name: null,
    option2_value: null,
    option3_name: null,
    option3_value: null,

    // ========== SEO Fields ==========
    seo_title: row['SEO Title (English)'] || null,
    seo_description: row['SEO Description (English)'] || null,
    path: row['Path'] || `/products/${handle}`,
    seo_keywords: row['SEO Keywords'] || null,

    // ========== Image Fields ==========
    images: images,
    image_alt: null, // SHOPLINE 1.0 doesn't have alt text
    image_position: 1,
    variant_image: images[0] || null,

    // ========== Inventory & Logistics ==========
    weight: parseNumeric(row['Weight(KG)'], null),
    weight_unit: 'kg', // SHOPLINE 1.0 uses KG
    barcode: row['Barcode'] || null,
    hs_code: null, // SHOPLINE 1.0 doesn't have HS code
    shipping_origin: null, // SHOPLINE 1.0 doesn't have this field
    fulfillment_service: null,
    inventory_tracker: null,
    inventory_policy: null,

    // ========== Other Fields ==========
    tags: tags,
    collections: collections,
    status: row['Hidden Product'] === 'Y' ? 'hidden' : 'active',
    published: row['Online Store Status'] === 'Y',
    tax_code: null,
    charge_tax: null,
    product_category: null,
    custom_product_type: null,
    cost_per_item: parseNumeric(row['Product Cost'], null),
    requires_shipping: null,
    gift_card: null,

    // ========== SHOPLINE 1.0 Specific Fields ==========
    member_price: parseNumeric(row['Member Price'], null),
    preorder: parseBool(row['Preorder Product']),
    unlimited_quantity: parseBool(row['Unlimited Quantity']),

    // ========== Metadata ==========
    platform: 'shopline-v1',
    updated_at: new Date().toISOString(),
    raw: row
  };
}

/**
 * Parse SHOPLINE 1.0 XLSX file
 * @param {ArrayBuffer} arrayBuffer - Raw file content as ArrayBuffer
 * @returns {Object} - Object containing headers, parsedData, and csvFormat
 */
export function parseShoplineV1Xlsx(arrayBuffer) {
  try {
    // Read workbook
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    if (!workbook.SheetNames.length) {
      throw new Error('Excel 文件中没有 Sheet');
    }

    // Get first worksheet
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];

    // Convert to JSON with raw: false to get formatted values
    // header: 1 generates array of arrays
    const rawSheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false });

    if (!rawSheetData || rawSheetData.length < 3) {
      throw new Error('Excel 文件数据不足，至少需要 3 行（英文表头、中文表头、数据行）');
    }

    // Row 0: English headers, Row 1: Chinese headers
    // We use English headers (row 0) as column names
    const headers = rawSheetData[0];

    // Data starts from row 2 (index 2)
    const dataRows = rawSheetData.slice(2);

    // Convert array rows to objects using headers
    const rawData = dataRows
      .filter(row => row && row.length > 0)
      .map((row, index) => {
        const obj = {};
        headers.forEach((header, i) => {
          if (header) {
            obj[header] = row[i] !== undefined ? row[i] : '';
          }
        });
        return obj;
      });

    // Normalize to unified product format
    const parsedData = rawData
      .map((row, index) => normalizeRow(row, index))
      .filter(product => product.id && product.id !== 'shopline-v1-product-undefined'); // Filter out empty rows

    return {
      csvFormat: 'shopline-v1',
      headers: headers.filter(h => h), // Remove null headers
      parsedData,
      rawData // Include raw data for preview
    };
  } catch (error) {
    throw new Error(`XLSX 解析失败: ${error.message}`);
  }
}

/**
 * Detect if a column list matches SHOPLINE 1.0 format
 * @param {string[]} columnNames - Array of column names
 * @returns {boolean} - True if matches SHOPLINE 1.0 format
 */
export function isShoplineV1Format(columnNames) {
  if (!Array.isArray(columnNames)) return false;
  return columnNames.includes(SHOPLINE_V1_SIGNATURE);
}

/**
 * Export for testing
 */
export { parseBool, parseNumeric, parseIntValue, parseTags, generateHandle, normalizeRow };
